import { useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { MailCheck, MailX } from "lucide-react";
import { AuthLayout } from "@/components/auth/AuthLayout";
import { Spinner } from "@/components/common/FullScreenLoader";
import { homeFor, useAuth } from "@/context/AuthContext";
import { api, apiError } from "@/lib/api";

export default function VerifyEmail() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const auth = useAuth();
  const [state, setState] = useState({ status: token ? "verifying" : "missing", message: "" });
  const started = useRef(false);

  useEffect(() => {
    if (!token || started.current) return;
    started.current = true;
    api
      .post("/auth/email/verify/confirm", { token })
      .then(() => {
        setState({ status: "verified", message: "" });
        if (auth.status === "authenticated") auth.refresh();
      })
      .catch((e) => setState({ status: "failed", message: apiError(e).message }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const continueTo = auth.status === "authenticated" ? homeFor(auth.user) : "/sign-in";

  return (
    <AuthLayout eyebrow="Email verification" title={state.status === "verified" ? "Email verified." : "Verifying your email."}>
      <div data-testid="verify-email-status" data-status={state.status} className="space-y-5">
        {state.status === "verifying" ? (
          <div className="flex items-center gap-3 text-sm text-slate-300">
            <Spinner className="h-4 w-4 text-electric" /> Confirming your link…
          </div>
        ) : null}
        {state.status === "verified" ? (
          <div className="flex items-center gap-3 rounded-md border border-emerald-500/30 bg-emerald-500/10 px-4 py-3 text-sm text-emerald-100">
            <MailCheck className="h-5 w-5 flex-none text-emerald-400" strokeWidth={1.75} />
            Your email address is confirmed.
          </div>
        ) : null}
        {state.status === "failed" || state.status === "missing" ? (
          <div className="flex items-center gap-3 bz-alert-error">
            <MailX className="h-5 w-5 flex-none" strokeWidth={1.75} />
            {state.message || "This verification link is missing its token."}
          </div>
        ) : null}
        {state.status !== "verifying" ? (
          <Link to={continueTo} data-testid="verify-email-continue-link" className="bz-btn-primary w-full">
            {auth.status === "authenticated" ? "Back to your account" : "Continue to sign in"}
          </Link>
        ) : null}
      </div>
    </AuthLayout>
  );
}
